import React, { useEffect, useState } from "react";
import { useApiClient } from "../contexts/ApiClientContext";
import { Outlet, useNavigate } from "react-router-dom";
import { User } from "../model/User";
import { useAuth } from "../contexts/AuthContext";
import TopMenu from "./TopMenu";
import LeftMenu from "./LeftMenu";
import APIEndPoints from "../utils/APIEndPoints";
import SETTINGS from "../globals";
import AppSections from "../utils/AppSections";
import EmbeddingToolInfo from "../utils/EmbeddingToolInfo";
import DialogWindow from "./common/dialog_window/DialogWindow";
import DialogWindowType from "./common/dialog_window/DialogWindowType";

// section that handles all Authentication-Protected areas
function Authenticated() {
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // cookies acceptance
  const cookiesAcceptedOn = new Date();
  cookiesAcceptedOn.setTime(
    parseInt(localStorage.getItem("cookies-accepted-on") ?? "0"),
  );
  const [cookiesAcceptanceStillValid, setCookiesAcceptanceStillValid] =
    useState<boolean>(
      EmbeddingToolInfo.isChemiTool() ||
        (new Date().getTime() - cookiesAcceptedOn.getTime()) / 1000 <
          24 * 60 * 60,
    );

  // get information about the authenticated user
  useEffect(() => {
    if (authManager.getUserID()) {
      apiClient
        ?.get<User>({
          url: APIEndPoints.USERS.ONE(authManager.getUserID()!!),
        })
        .then((data: User) => {
          authManager.setUserInfo(data);
          authManager.setUserName(data.name + " " + data.surname);
          authManager.setBoardMember(data.board_member);
          setLoading(false);
        })
        .catch(() => {
          navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
        });
    }
  }, [apiClient, authManager]);

  const leftMenuCSS = authManager.isAdmin() ? "ml-56" : "";

  return (
    <div>
      <TopMenu />
      <div className="mt-24">
        {authManager.isAdmin() && <LeftMenu />}
        <div className={"p-4 pb-24 " + leftMenuCSS}>
          <Outlet />
        </div>
        <div className="mb-10 mr-10 text-right">
          <a
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections.TERMS_AND_CONDITIONS,
                }),
              )
            }
            className="inline-block cursor-pointer hover:to-blue-600"
          >
            Terms and Conditions
          </a>
          {" | "}
          <a
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections.COMMUNITY_RULES,
                }),
              )
            }
            className="inline-block cursor-pointer hover:to-blue-600"
          >
            Community Rules
          </a>
          {" | "}
          <a
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections.LICENSE_AGREEMENT,
                }),
              )
            }
            className="inline-block cursor-pointer hover:to-blue-600"
          >
            Licensing Agreement
          </a>
        </div>
      </div>
      {!cookiesAcceptanceStillValid && (
        <DialogWindow
          type={DialogWindowType.INFO}
          message="ChemiNova Portal is storing on your device the minimum information required for proper functioning. By continuing the use of the application, you agree with these terms."
          onConfirm={() => {
            localStorage.setItem(
              "cookies-accepted-on",
              new Date().getTime().toString(),
            );
            setCookiesAcceptanceStillValid(true);
          }}
          confirmLabel="I agree"
        />
      )}
    </div>
  );
}

export default Authenticated;
